/**
 * Подбор: запуск, статус, результаты (BE-080, BE-087).
 *
 * Три вещи, из-за которых здесь больше кода, чем «просто fetch»:
 *
 * * **идемпотентность запуска.** `POST /confirm` требует `Idempotency-Key`, и
 *   ключ обязан пережить перезагрузку вкладки: повтор после обрыва сети должен
 *   вернуть тот же прогон, а не поставить в очередь второй (MVP_TECH_RFC §21);
 * * **polling.** Пока прогон `queued|processing`, статус опрашивается раз в
 *   секунду и прекращается на `completed|failed` (§22). Интервал задаётся
 *   функцией от данных, а не таймером в компоненте: остановка не должна
 *   зависеть от того, отрисован ли экран ожидания;
 * * **результаты кешируются, статус — нет.** Завершённый прогон неизменен,
 *   поэтому его страница живёт 60 секунд; статус читается каждый раз.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';
import { randomUuid } from '@shared/lib/uuid';

import type {
  ListingOpenedVia,
  SearchResultDetailItem,
  SearchResultsPageData,
  SearchRunAccepted,
  SearchRunState,
} from './types';

/** MVP_TECH_RFC §22: завершённые результаты живут в кеше 60 секунд. */
export const SEARCH_RESULTS_STALE_TIME_MS = 60_000;

/** §22: `queued`/`processing` — опрос раз в секунду. */
export const SEARCH_RUN_POLL_INTERVAL_MS = 1_000;

/** MVP_PRD §19: двенадцать результатов на страницу (`SEARCH_RESULTS_PAGE_SIZE`). */
export const SEARCH_RESULTS_PAGE_SIZE = 12;

const CONFIRM_KEY_STORAGE = 'carviq.search-confirm-key';

/**
 * Ключ идемпотентности запуска, привязанный к версии критериев.
 *
 * Привязка обязательна с обеих сторон: один ключ на все запуски склеил бы
 * подбор по новым критериям со старым прогоном, а новый ключ на каждый клик
 * создавал бы по прогону на нажатие.
 */
export function confirmIdempotencyKey(criteriaVersionId: string, restart = false): string {
  const fallback = randomUuid();
  try {
    const raw = sessionStorage.getItem(CONFIRM_KEY_STORAGE);
    if (raw !== null && !restart) {
      const parsed = JSON.parse(raw) as { versionId?: unknown; key?: unknown };
      if (parsed.versionId === criteriaVersionId && typeof parsed.key === 'string') {
        return parsed.key;
      }
    }
    sessionStorage.setItem(
      CONFIRM_KEY_STORAGE,
      JSON.stringify({ versionId: criteriaVersionId, key: fallback }),
    );
  } catch {
    // Приватный режим Safari запрещает запись: идемпотентность в пределах
    // одного вызова сохраняется, между перезагрузками — нет.
  }
  return fallback;
}

export interface ConfirmSearchInput {
  sessionId: string;
  /** Версия, которую видел пользователь: несовпадение backend отклонит (409). */
  criteriaVersionId: string;
  /**
   * Повтор после неудачного прогона.
   *
   * Прежний ключ идемпотентности вернул бы тот же упавший прогон, поэтому
   * «Повторить» осознанно берёт новый ключ. Обычный переход к результатам —
   * никогда: там повтор обязан вернуть существующий прогон.
   */
  restart?: boolean | undefined;
}

/**
 * Подтверждение критериев и запуск подбора.
 *
 * Backend отвечает `202` и ставит job: результатов в ответе нет и быть не может.
 * Поиск разрешён только при `readiness_score = 100` — кнопку это уже учитывает,
 * но арбитр всё равно backend (`409 CRITERIA_NOT_READY`).
 */
export function useConfirmSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ConfirmSearchInput) =>
      apiFetch<SearchRunAccepted>(
        `/search-sessions/${encodeURIComponent(input.sessionId)}/confirm`,
        {
          method: 'POST',
          idempotencyKey: confirmIdempotencyKey(input.criteriaVersionId, input.restart ?? false),
          body: { criteria_version_id: input.criteriaVersionId },
        },
      ),
    onSuccess: async (_accepted, input) => {
      // Запрос перешёл в `running` и получил `current_search_run_id`;
      // список «Мои заказы» показывает статус без чтения прогонов.
      //
      // **Перечитать заказ нужно до перехода, а не параллельно с ним.** Стадию
      // двигает backend (`ready → analyzing`), а навигационный guard экрана
      // читает её из кеша: с прежней стадией `ready` он немедленно уводил бы
      // обратно на диалог — «нажал, страница моргнула, ничего не открылось, и
      // надо нажимать второй раз». Ожидание здесь держит кнопку в состоянии
      // «Запускаем подбор …», поэтому пауза видна и объяснена.
      await queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(input.sessionId) });
      void queryClient.invalidateQueries({ queryKey: ['search-sessions'] });
    },
  });
}

export interface GeoExpansionInput {
  sessionId: string;
  /** `true` — согласие: создаётся новый прогон той же версии критериев. */
  accepted: boolean;
  /**
   * Ступень, о которой спрашивал Karl.
   *
   * Ключ идемпотентности выводится из неё: повтор после обрыва сети обязан
   * вернуть **тот же** прогон, а согласие на следующую ступень — поставить
   * новый. Один ключ на все ответы склеил бы две разные ступени в одну.
   */
  step: number;
}

export interface GeoExpansionResult {
  accepted: boolean;
  geo_expansion_step: number;
  /** `null` — пользователь отказался: прогон не создавался. */
  search_run_id: string | null;
}

const GEO_EXPANSION_KEY_STORAGE = 'carviq.geo-expansion-key';

function geoExpansionIdempotencyKey(sessionId: string, step: number): string {
  const slot = `${sessionId}:${String(step)}`;
  const fallback = randomUuid();
  try {
    const raw = sessionStorage.getItem(GEO_EXPANSION_KEY_STORAGE);
    if (raw !== null) {
      const parsed = JSON.parse(raw) as { slot?: unknown; key?: unknown };
      if (parsed.slot === slot && typeof parsed.key === 'string') return parsed.key;
    }
    sessionStorage.setItem(GEO_EXPANSION_KEY_STORAGE, JSON.stringify({ slot, key: fallback }));
  } catch {
    // Приватный режим Safari запрещает запись: идемпотентность в пределах
    // одного вызова сохраняется, между перезагрузками — нет.
  }
  return fallback;
}

/**
 * Ответ на предложение расширить географию (BE-395, MVP_PRD §43.3).
 *
 * Согласие создаёт **новый прогон той же версии критериев**: это тот же заказ,
 * оплата не запрашивается. Отказ фиксируется, и вопрос не повторяется до
 * следующей версии критериев — поэтому оба исхода уходят на сервер, а не
 * прячут блок локально.
 */
export function useGeoExpansion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: GeoExpansionInput) =>
      apiFetch<GeoExpansionResult>(
        `/search-sessions/${encodeURIComponent(input.sessionId)}/geo-expansion`,
        {
          method: 'POST',
          idempotencyKey: geoExpansionIdempotencyKey(input.sessionId, input.step),
          body: { accepted: input.accepted },
        },
      ),
    onSuccess: async (_result, input) => {
      // Как и у подтверждения критериев: согласие создаёт новый прогон и
      // двигает стадию заказа, а экран анализа открывается сразу после. Пока
      // кеш держит прежнюю стадию, guard возвращает пользователя назад.
      await queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(input.sessionId) });
      void queryClient.invalidateQueries({ queryKey: ['search-sessions'] });
    },
  });
}

/** Прогон ещё выполняется — значит, статус нужно перечитать. */
function isRunning(run: SearchRunState | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'processing';
}

/**
 * Статус прогона. Опрос прекращается сам, когда прогон завершился.
 *
 * `staleTime = 0`: показывать закешированный `queued` после возврата на экран
 * означало бы врать про этап работы.
 */
export function useSearchRun(runId: string | null): UseQueryResult<SearchRunState> {
  return useQuery({
    queryKey: queryKeys.searchRun(runId ?? ''),
    queryFn: ({ signal }) =>
      apiFetch<SearchRunState>(`/search-runs/${encodeURIComponent(runId ?? '')}`, { signal }),
    enabled: runId !== null,
    staleTime: 0,
    refetchInterval: (query) =>
      isRunning(query.state.data) ? SEARCH_RUN_POLL_INTERVAL_MS : (false as const),
  });
}

/** Дополнительные фильтры результатов (MVP_PRD §19). Hard-критерии не ослабляют. */
export interface SearchResultsQuery {
  page?: number | undefined;
  pageSize?: number | undefined;
  sort?: string | undefined;
  make?: string | undefined;
  model?: string | undefined;
  priceMinCents?: number | undefined;
  priceMaxCents?: number | undefined;
  yearMin?: number | undefined;
  mileageMaxKm?: number | undefined;
  fuelTypes?: readonly string[] | undefined;
  transmissions?: readonly string[] | undefined;
  drivetrains?: readonly string[] | undefined;
  featureCodes?: readonly string[] | undefined;
  withoutAttention?: boolean | undefined;
  minScore?: number | undefined;
  /** Только отмеченные автомобили рабочего набора — «Meine Favoriten» (BE-581). */
  favoritesOnly?: boolean | undefined;
}

/**
 * Параметры запроса результатов.
 *
 * Набор строится детерминированно: он же входит в ключ кеша, и одинаковый
 * фильтр обязан давать одинаковый ключ.
 */
export function searchResultParams(query: SearchResultsQuery): [string, string][] {
  const params: [string, string][] = [];
  const add = (key: string, value: string | number | undefined) => {
    if (value !== undefined && value !== '') params.push([key, String(value)]);
  };

  add('page', query.page);
  add('page_size', query.pageSize);
  add('sort', query.sort);
  add('make', query.make);
  add('model', query.model);
  add('price_min', query.priceMinCents);
  add('price_max', query.priceMaxCents);
  add('year_min', query.yearMin);
  add('mileage_max', query.mileageMaxKm);
  add('min_score', query.minScore);
  if (query.withoutAttention) params.push(['without_attention', 'true']);
  if (query.favoritesOnly) params.push(['favorites_only', 'true']);

  for (const value of query.fuelTypes ?? []) params.push(['fuel', value]);
  for (const value of query.transmissions ?? []) params.push(['transmission', value]);
  for (const value of query.drivetrains ?? []) params.push(['drivetrain', value]);
  for (const value of query.featureCodes ?? []) params.push(['features', value]);

  return params;
}

/**
 * Страница результатов прогона.
 *
 * Запрос уходит только у завершённого прогона: у `queued|processing` backend
 * отвечает `409`, и опрашивать его списком вместо статуса незачем.
 */
export function useSearchResults(
  runId: string | null,
  query: SearchResultsQuery,
  enabled: boolean,
): UseQueryResult<SearchResultsPageData> {
  const params = searchResultParams(query);

  return useQuery({
    queryKey: queryKeys.searchResults(runId ?? '', Object.fromEntries(params)),
    queryFn: ({ signal }) => {
      const search = new URLSearchParams(params).toString();
      return apiFetch<SearchResultsPageData>(
        `/search-runs/${encodeURIComponent(runId ?? '')}/results${search ? `?${search}` : ''}`,
        { signal },
      );
    },
    enabled: enabled && runId !== null,
    staleTime: SEARCH_RESULTS_STALE_TIME_MS,
    // Смена страницы и фильтра не должна мигать скелетоном поверх готового
    // списка: прошлые карточки остаются, пока грузятся новые.
    placeholderData: (previous) => previous,
  });
}

/**
 * Персональная карточка автомобиля внутри прогона (FE-083, BE-580).
 *
 * До оплаты backend отвечает `access: open` — без разбора соответствия, — после
 * оплаты прежней полной карточкой. Параметры списка (`sort`, фильтры,
 * `favorites_only`) передаются затем, чтобы `navigation` («Auto 3 von 100»)
 * считалась в том же порядке, что видел человек: страница в навигацию не
 * входит, поэтому в запрос не уходит.
 */
export function useSearchResultDetail(
  runId: string | null,
  vehicleId: string | null,
  query: Omit<SearchResultsQuery, 'page' | 'pageSize'> = {},
): UseQueryResult<SearchResultDetailItem> {
  const params = searchResultParams(query);

  return useQuery({
    queryKey: queryKeys.searchResult(runId ?? '', vehicleId ?? '', Object.fromEntries(params)),
    queryFn: ({ signal }) => {
      const search = new URLSearchParams(params).toString();
      return apiFetch<SearchResultDetailItem>(
        `/search-runs/${encodeURIComponent(runId ?? '')}/results/${encodeURIComponent(
          vehicleId ?? '',
        )}${search ? `?${search}` : ''}`,
        { signal },
      );
    },
    enabled: runId !== null && vehicleId !== null,
    staleTime: SEARCH_RESULTS_STALE_TIME_MS,
  });
}

// --- Избранное (BE-581) ------------------------------------------------------------------

export interface SetFavoriteInput {
  /** Заказ: отметка — закладка на заказе, а не на прогоне. */
  searchId: string;
  /** Прогон, чьи закешированные страницы показывают эту отметку. */
  runId: string;
  vehicleId: string;
  /** `true` — отметить (`PUT`), `false` — снять (`DELETE`). */
  favorite: boolean;
}

/** Снимок закешированных ответов прогона на время запроса — для отката. */
type FavoriteSnapshot = [QueryKey, unknown][];

function withFavoriteOnPage(
  page: SearchResultsPageData,
  vehicleId: string,
  favorite: boolean,
): SearchResultsPageData {
  const target = page.items.find((item) => item.vehicle_id === vehicleId);
  // Страница меняется, только если отметка на ней и правда меняется: на других
  // страницах прогона автомобиля нет, а повтор не считается дважды.
  if (target === undefined || target.access === 'teaser' || target.is_favorite === favorite) {
    return page;
  }
  const delta = favorite ? 1 : -1;
  return {
    ...page,
    items: page.items.map((item) =>
      item.vehicle_id === vehicleId && item.access !== 'teaser'
        ? { ...item, is_favorite: favorite }
        : item,
    ),
    favorites_count: Math.max(0, page.favorites_count + delta),
  };
}

function isResultsPage(value: unknown): value is SearchResultsPageData {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { items?: unknown }).items) &&
    typeof (value as { favorites_count?: unknown }).favorites_count === 'number'
  );
}

function isDetail(value: unknown): value is SearchResultDetailItem {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { is_favorite?: unknown }).is_favorite === 'boolean'
  );
}

/** Отметка в кеше всех страниц и карточек прогона сразу — тем же значением. */
function applyFavorite(client: QueryClient, input: SetFavoriteInput): FavoriteSnapshot {
  const resultsKey = ['search-run', input.runId, 'results'];
  const detailKey = ['search-run', input.runId, 'result', input.vehicleId];
  const snapshot: FavoriteSnapshot = [
    ...client.getQueriesData({ queryKey: resultsKey }),
    ...client.getQueriesData({ queryKey: detailKey }),
  ];

  client.setQueriesData<unknown>({ queryKey: resultsKey }, (page: unknown) =>
    isResultsPage(page) ? withFavoriteOnPage(page, input.vehicleId, input.favorite) : page,
  );
  client.setQueriesData<unknown>({ queryKey: detailKey }, (detail: unknown) =>
    isDetail(detail) ? { ...detail, is_favorite: input.favorite } : detail,
  );
  return snapshot;
}

/**
 * Отметить автомобиль или снять отметку (BE-581, FE-586).
 *
 * **Сразу, без двух шагов и с откатом.** Отметка обратима и ничего не
 * отправляет наружу, поэтому сердечко меняется в момент нажатия (CLAUDE.md §8
 * требует двух шагов только у необратимых действий). Кеш правится во всех
 * страницах прогона и в карточке автомобиля разом: иначе «Meine Favoriten (n)»
 * и сердечко на соседнем экране расходились бы до перезагрузки. При сбое кеш
 * возвращается к снимку, а сообщение показывает вызывающий экран — у мутации
 * нет своего места на экране.
 *
 * После ответа — в любом исходе — страницы прогона и карточка перечитываются:
 * счётчик и выдача с `favorites_only` считает сервер, а не эта функция.
 */
export function useSetFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SetFavoriteInput) =>
      apiFetch<undefined>(
        `/search-sessions/${encodeURIComponent(input.searchId)}/favorites/${encodeURIComponent(
          input.vehicleId,
        )}`,
        { method: input.favorite ? 'PUT' : 'DELETE' },
      ),
    onMutate: async (input) => {
      // Опрос, закончившийся после отметки, перезаписал бы её прежним
      // значением: сердечко мигнуло бы обратно без всякой ошибки.
      await queryClient.cancelQueries({ queryKey: ['search-run', input.runId, 'results'] });
      await queryClient.cancelQueries({
        queryKey: ['search-run', input.runId, 'result', input.vehicleId],
      });
      return { snapshot: applyFavorite(queryClient, input) };
    },
    onError: (_error, _input, context) => {
      for (const [key, data] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: ['search-run', input.runId, 'results'] });
      void queryClient.invalidateQueries({
        queryKey: ['search-run', input.runId, 'result', input.vehicleId],
      });
    },
  });
}

// --- Переход на объявление автохауса (BE-582) -------------------------------------------

export interface ListingOpenedInput {
  runId: string;
  vehicleId: string;
  via: ListingOpenedVia;
}

/**
 * Сообщить, что пользователь ушёл на объявление автохауса (BE-582).
 *
 * **Отправить и забыть.** Ссылка открывается сразу и сама, событие уходит
 * параллельно и переход не задерживает: ждать ответа нечего, а сбой здесь —
 * потерянная строка статистики, а не ошибка пользователя. `keepalive` держит
 * запрос живым, если вкладка с витриной закроется сразу после нажатия.
 */
export function reportListingOpened({ runId, vehicleId, via }: ListingOpenedInput): void {
  apiFetch<undefined>(
    `/search-runs/${encodeURIComponent(runId)}/results/${encodeURIComponent(
      vehicleId,
    )}/listing-opened`,
    { method: 'POST', body: { via }, keepalive: true },
  ).catch(() => {
    // Статистика не стоит сообщения об ошибке: переход уже состоялся.
  });
}

/**
 * То же в виде хука — для экранов, которые берут действия из хуков. Ссылка
 * стабильна: это функция модуля, а не замыкание компонента.
 */
export function useReportListingOpened(): (input: ListingOpenedInput) => void {
  return reportListingOpened;
}
